import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { useATS } from "../context/ATSContext";
import { activityService } from "../services/activityService";
import { jobService } from "../services/jobService";

// What the team did, day by day, across every job.
//
// ─── WHY THIS SCREEN EXISTS ─────────────────────────────────────────────────
//
// There were already two views of the activity table and neither answers the
// question somebody opens a hiring tool to ask on a Monday morning:
//
//   the row timeline    "where is this person up to"
//   the candidate trail "who turned them down, and what did they say"
//   THIS                "what did we DO last week"
//
// How many resumes were read and on which jobs. How many interview links went
// out and to whom. How many people were turned down and why. Who moved, and
// into which round. That is answered by day, across jobs, or not at all —
// reading it out of sixteen candidate drawers is not reading it.
//
// ─── WHY THE ENTRIES ARE A GRID AND NOT A FLEX ROW ──────────────────────────
//
// The first version laid each entry out with flex-wrap, so the candidate's name
// started at a different x on every line depending on how long the action label
// was. A log is read by scanning ONE column at a time — "which of these were
// rejections", "what did Aman do" — and that only works if the columns line up.
// Fixed grid tracks, `tabular-nums` on the times, and the table scrolls
// sideways on a narrow screen rather than reflowing into something unscannable.

const FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

// The module's own tokens, taken from JobDetails so this screen belongs to the
// same product rather than looking like a page from a different one.
const T = {
  pageBg: "#f7f9fc",
  card: "#ffffff",
  cardLine: "#edf1f7",
  line: "#d0d5dd",
  ink: "#101828",
  strong: "#344054",
  body: "#475467",
  muted: "#667085",
  faint: "#98a2b3",
  wash: "#f9fafb",
  accent: "#0b8a8f",
  accentSoft: "#ecfdff",
  danger: "#b42318",
  dangerSoft: "#fef3f2",
  dangerLine: "#fecdca",
  warnInk: "#b54708",
  warnSoft: "#fffaeb",
  warnLine: "#fedf89",
  goodInk: "#067647",
  goodSoft: "#ecfdf3",
};

// Every action gets a family, and the family gets a colour. Four meanings, not
// twelve: something arrived, something progressed, something went backwards,
// somebody recorded something. A reader scanning a day wants to see the shape
// of it before reading a word.
const FAMILY = {
  FORM_IMPORTED: "arrive",
  AI_SCREENED: "progress",
  STAGE_MOVED: "progress",
  INVITE_ISSUED: "progress",
  EMAIL_SENT: "progress",
  SLOT_BOOKED: "progress",
  AI_INTERVIEW_COMPLETED: "progress",
  INTERVIEW_COMPLETED: "progress",
  SHORTLISTED: "good",
  REJECTED: "setback",
  NOTE_ADDED: "admin",
  ASSESSMENT_UPLOADED: "admin",
};
const FAMILY_COLOR = {
  arrive: "#5f6bd6",
  progress: T.accent,
  good: T.goodInk,
  setback: T.danger,
  admin: T.faint,
};
const familyOf = (type) => FAMILY[type] || "admin";
const colorOf = (type) => FAMILY_COLOR[familyOf(type)];

// The order a day reads in — the shape of the pipeline, not alphabetical and
// not by size. "12 applied · 8 read · 3 links · 1 rejected" tells a story; the
// same numbers sorted by count do not.
const ORDER = [
  "FORM_IMPORTED",
  "AI_SCREENED",
  "STAGE_MOVED",
  "INVITE_ISSUED",
  "EMAIL_SENT",
  "AI_INTERVIEW_COMPLETED",
  "INTERVIEW_COMPLETED",
  "SHORTLISTED",
  "REJECTED",
  "NOTE_ADDED",
  "ASSESSMENT_UPLOADED",
  "SLOT_BOOKED",
];

// Tiles worth a headline number. Deliberately not every type — a row of twelve
// numbers is a wall, and these five are the ones a manager is counting.
const TILES = [
  ["FORM_IMPORTED", "Applied"],
  ["AI_SCREENED", "Resumes read"],
  ["INVITE_ISSUED", "Links sent"],
  ["STAGE_MOVED", "Rounds moved"],
  ["REJECTED", "Rejected"],
];

const PRESETS = [
  ["Today", 1],
  ["7 days", 7],
  ["14 days", 14],
  ["30 days", 30],
];

// The grid. One place, so the header and every row cannot disagree about where
// a column starts — which is the whole reason this is a grid.
const GRID = "64px 178px minmax(150px,1.1fr) minmax(120px,0.9fr) minmax(150px,1fr) 150px";
const TABLE_MIN = 940;

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function dayHeading(key) {
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  const today = new Date();
  const yd = new Date(today);
  yd.setDate(yd.getDate() - 1);
  if (key === iso(today)) return "Today";
  if (key === iso(yd)) return "Yesterday";
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: d.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}
const subHeading = (key) => {
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yd = new Date(today);
  yd.setDate(yd.getDate() - 1);
  if (key !== iso(today) && key !== iso(yd)) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};
const clock = (at) =>
  new Date(at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });

// One stylesheet, injected once, following the pattern JobDetails established
// so the two screens share their tokens rather than each carrying a copy in sx.
if (typeof document !== "undefined" && !document.getElementById("ats-activity-log-ui")) {
  const style = document.createElement("style");
  style.id = "ats-activity-log-ui";
  style.textContent = `
  @keyframes alFade{ from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .al-page{ padding:14px 16px 28px; background:${T.pageBg}; min-height:100vh; font-family:${FONT}; }
  .al-in{ animation:alFade .35s ease both; }

  .al-card{
    background:${T.card};
    border:1px solid ${T.cardLine};
    border-radius:10px;
    box-shadow:none;
  }

  .al-btn{
    height:36px;
    border-radius:9px !important;
    border:1px solid ${T.line} !important;
    background:${T.card} !important;
    color:${T.strong} !important;
    padding:0 14px !important;
    text-transform:none !important;
    font-weight:700 !important;
    font-size:13px !important;
    font-family:${FONT} !important;
    box-shadow:none !important;
    white-space:nowrap;
  }
  .al-btn:hover{ border-color:${T.accent} !important; color:${T.accent} !important; }
  .al-btn.Mui-disabled{ color:${T.faint} !important; border-color:${T.cardLine} !important; }

  /* The preset rail — the same pill group the job page uses for its tabs, so
     "pick a range" and "pick a tab" look like the same kind of control. */
  .al-rail{
    display:inline-flex; gap:2px; align-items:center; height:36px;
    padding:3px; border-radius:9px; background:#eef2f6;
  }
  .al-pill{
    display:flex; align-items:center; height:100%; padding:0 13px;
    font-size:12.5px; font-weight:650; color:${T.body};
    border-radius:7px; cursor:pointer; user-select:none;
    transition:background .15s, color .15s;
  }
  .al-pill:hover{ color:${T.ink}; }
  .al-pill[data-on="true"]{ background:${T.card}; color:${T.accent}; box-shadow:0 1px 3px rgba(16,24,40,.06); }

  /* Sticky day header. On a 30-day window you are ten screens down and still
     need to know which day you are reading. */
  .al-dayhead{
    position:sticky; top:0; z-index:2;
    display:flex; align-items:baseline; gap:10px; flex-wrap:wrap;
    padding:9px 2px 9px;
    background:${T.pageBg};
    border-bottom:1px solid ${T.cardLine};
    margin-bottom:8px;
  }

  .al-grid{ display:grid; grid-template-columns:${GRID}; align-items:center; gap:12px; }

  .al-head{
    font-size:10.5px; font-weight:800; letter-spacing:.06em; text-transform:uppercase;
    color:${T.faint}; padding:9px 14px; border-bottom:1px solid ${T.cardLine};
    background:${T.wash};
  }

  .al-row{ padding:11px 14px; border-top:1px solid ${T.cardLine}; transition:background .12s; }
  .al-row:first-of-type{ border-top:none; }
  .al-row:hover{ background:${T.wash}; }

  .al-time{ font-size:12px; color:${T.faint}; font-variant-numeric:tabular-nums; }
  .al-action{ font-size:13px; font-weight:700; display:flex; align-items:center; gap:7px; min-width:0; }
  .al-dot{ width:7px; height:7px; border-radius:50%; flex-shrink:0; }
  .al-name{ font-size:13px; font-weight:600; color:${T.strong}; cursor:pointer; }
  .al-name:hover{ color:${T.accent}; }
  .al-job{ font-size:12px; color:${T.muted}; }
  .al-job:hover{ color:${T.accent}; }
  .al-move{ font-size:12px; color:${T.muted}; display:flex; align-items:center; gap:4px; min-width:0; }

  .al-chip{
    display:inline-flex; align-items:center; gap:4px;
    height:20px; padding:0 8px; border-radius:6px;
    font-size:10.5px; font-weight:700; white-space:nowrap;
  }

  /* The detail line sits under the row, indented to the action column so it
     reads as belonging to that action rather than starting a new one. */
  .al-detail{
    grid-column:2 / -1;
    font-size:12px; line-height:1.6; color:${T.muted};
    margin-top:5px; max-width:900px;
  }
  .al-quote{
    grid-column:2 / -1;
    margin-top:6px; max-width:900px;
    border-left:2px solid ${T.cardLine}; padding:2px 0 2px 10px;
    font-size:12.5px; line-height:1.65; color:${T.body}; white-space:pre-wrap;
  }
  .al-quote[data-neg="true"]{ border-left-color:${T.dangerLine}; }
  `;
  document.head.appendChild(style);
}

function Chip({ tone = "neutral", icon, children, title, onClick }) {
  const tones = {
    neutral: { bg: T.wash, fg: T.muted, bd: T.cardLine },
    accent: { bg: T.accentSoft, fg: T.accent, bd: "#c5eff1" },
    warn: { bg: T.warnSoft, fg: T.warnInk, bd: T.warnLine },
    danger: { bg: T.dangerSoft, fg: T.danger, bd: T.dangerLine },
    info: { bg: "#eef4ff", fg: "#3538cd", bd: "#c7d7fe" },
  };
  const c = tones[tone] || tones.neutral;
  const el = (
    <Box
      className="al-chip"
      onClick={onClick}
      sx={{
        bgcolor: c.bg,
        color: c.fg,
        border: `1px solid ${c.bd}`,
        cursor: onClick ? "pointer" : "default",
        maxWidth: "100%",
        "& > span": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      }}
    >
      {icon}
      <span>{children}</span>
    </Box>
  );
  return title ? <Tooltip title={title}>{el}</Tooltip> : el;
}

export default function ActivityLog() {
  const navigate = useNavigate();
  const { store_id } = useATS();

  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 13);
    return { from: iso(from), to: iso(to) };
  });
  const [jobId, setJobId] = useState("");
  const [type, setType] = useState("");
  const [jobs, setJobs] = useState([]);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await activityService.storeLog({
        from: range.from,
        to: range.to,
        ...(jobId ? { job_id: jobId } : {}),
        ...(type ? { type } : {}),
      });
      const body = res?.data;
      if (!body?.success) throw new Error(body?.statusMessage || "Could not load the log.");
      setState({ loading: false, error: null, data: body.data });
    } catch (e) {
      setState({
        loading: false,
        error: e?.response?.data?.statusMessage || e?.message || "Could not load the log.",
        data: null,
      });
    }
  }, [range.from, range.to, jobId, type]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (store_id === null || store_id === undefined) return;
    jobService
      .getAllJobs(store_id)
      .then((r) => setJobs(r?.data?.data || []))
      // A log that cannot offer a job filter is still a usable log.
      .catch(() => setJobs([]));
  }, [store_id]);

  const data = state.data;
  const labels = useMemo(() => data?.labels || {}, [data]);

  // Which preset the current range corresponds to, so the rail reflects the
  // dates rather than only the last button pressed — the date inputs can move
  // the range out from under it.
  const activePreset = useMemo(() => {
    const to = new Date(`${range.to}T00:00:00`);
    const from = new Date(`${range.from}T00:00:00`);
    if (iso(to) !== iso(new Date())) return null;
    const days = Math.round((to - from) / 86400000) + 1;
    return PRESETS.some(([, d]) => d === days) ? days : null;
  }, [range]);

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    setRange({ from: iso(from), to: iso(to) });
  };

  const summaryLine = (totals) => {
    const known = ORDER.filter((t) => totals[t]);
    const rest = Object.keys(totals).filter((t) => !ORDER.includes(t));
    return [...known, ...rest]
      .map((t) => `${totals[t]} ${(labels[t] || t).toLowerCase()}`)
      .join("   ·   ");
  };

  // Downloads the window as CSV. Deliberately not the xlsx workbook the
  // candidate list uses: that one is a report per round with 21 columns, and
  // this is a flat chronological log — one sheet, one row per action, which is
  // what somebody pastes into a weekly update.
  const exportCsv = () => {
    if (!data) return;
    const rows = data.days.flatMap((d) => d.entries);
    const head = ["Date", "Time", "Action", "Candidate", "Job", "From round", "To round", "By", "Detail", "Note"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = rows.map((e) => {
      const dt = new Date(e.at);
      return [
        `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()}`,
        clock(e.at),
        e.label,
        e.candidate_name,
        e.job_title,
        e.from_stage || "",
        e.to_stage || "",
        e.operator ? e.operator.name : e.by_operator_expected ? "not recorded" : "system",
        e.detail || "",
        e.note || "",
      ].map(esc).join(",");
    });
    // A BOM, so Excel opens a UTF-8 CSV as UTF-8 instead of mangling every
    // name with an accent in it.
    const blob = new Blob([`﻿${[head.map(esc).join(","), ...body].join("\r\n")}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity_${data.range.from}_to_${data.range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalActions = data?.entries_shown ?? 0;

  return (
    <Box className="al-page">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        className="al-in"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: T.faint, fontWeight: 500 }}>
            Hiring &nbsp;&gt;&nbsp; Activity log
          </Typography>
          <Typography
            sx={{ fontFamily: FONT, fontSize: 23, fontWeight: 800, color: T.ink, letterSpacing: "-0.4px", mt: 0.25 }}
          >
            Activity log
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: T.muted, mt: 0.25 }}>
            Every action across every job, newest first — resumes read, links
            sent, rounds moved, offers, joinings, and each rejection with its
            reason and who made it.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            className="al-btn"
            onClick={exportCsv}
            disabled={!totalActions}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />}
          >
            Export CSV
          </Button>
          <Button
            className="al-btn"
            onClick={load}
            disabled={state.loading}
            startIcon={
              state.loading ? (
                <CircularProgress size={13} sx={{ color: T.faint }} />
              ) : (
                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
              )
            }
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <Box className="al-card al-in" sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", flexWrap: "wrap", rowGap: 1.25 }}>
          <Box className="al-rail">
            {PRESETS.map(([label, days]) => (
              <Box
                key={label}
                className="al-pill"
                data-on={activePreset === days}
                onClick={() => applyPreset(days)}
              >
                {label}
              </Box>
            ))}
          </Box>

          <TextField
            type="date"
            size="small"
            value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            inputProps={{ max: range.to }}
            sx={{ "& .MuiOutlinedInput-root": { height: 36, borderRadius: "9px", fontSize: 12.5, fontFamily: FONT } }}
          />
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: T.faint }}>to</Typography>
          <TextField
            type="date"
            size="small"
            value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            inputProps={{ min: range.from, max: iso(new Date()) }}
            sx={{ "& .MuiOutlinedInput-root": { height: 36, borderRadius: "9px", fontSize: 12.5, fontFamily: FONT } }}
          />

          <TextField
            select
            size="small"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 186, "& .MuiOutlinedInput-root": { height: 36, borderRadius: "9px", fontSize: 12.5, fontFamily: FONT } }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>All jobs</MenuItem>
            {jobs.map((j) => (
              <MenuItem key={j.id} value={String(j.id)} sx={{ fontSize: 13 }}>
                {j.title}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            value={type}
            onChange={(e) => setType(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 186, "& .MuiOutlinedInput-root": { height: 36, borderRadius: "9px", fontSize: 12.5, fontFamily: FONT } }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>All actions</MenuItem>
            {/* Driven by the server's own label map, so a new activity type
                becomes filterable without editing this file. */}
            {ORDER.filter((t) => labels[t]).map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                {labels[t]}
              </MenuItem>
            ))}
          </TextField>

          {(jobId || type) && (
            <Button
              className="al-btn"
              onClick={() => {
                setJobId("");
                setType("");
              }}
              sx={{ border: "none !important" }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Loading: skeletons, not a spinner on an empty page ─────────────
          The layout is known before the data arrives, so showing its shape is
          more informative than a spinner and does not make the page jump when
          the rows land. */}
      {state.loading && !data && (
        <Box className="al-in">
          <Box className="al-card" sx={{ p: 2, mb: 2, display: "flex", gap: 4 }}>
            {TILES.map(([k]) => (
              <Box key={k}>
                <Skeleton variant="text" width={38} height={30} />
                <Skeleton variant="text" width={80} height={16} />
              </Box>
            ))}
          </Box>
          <Box className="al-card" sx={{ p: 2 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="text" height={30} sx={{ mb: 0.5 }} />
            ))}
          </Box>
        </Box>
      )}

      {state.error && (
        <Alert
          severity="warning"
          sx={{ borderRadius: "10px", fontFamily: FONT, fontSize: 13, mb: 2 }}
          action={
            <Button size="small" onClick={load} sx={{ textTransform: "none", fontWeight: 700 }}>
              Retry
            </Button>
          }
        >
          {state.error}
        </Alert>
      )}

      {data && (
        <>
          {/* ── Tiles ───────────────────────────────────────────────────── */}
          <Box
            className="al-card al-in"
            sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", gap: { xs: 3, md: 5 }, flexWrap: "wrap", rowGap: 2 }}
          >
            {TILES.map(([key, label]) => {
              const n = data.totals[key] || 0;
              return (
                <Box key={key} sx={{ minWidth: 96 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 24,
                      fontWeight: 800,
                      lineHeight: 1.15,
                      letterSpacing: "-0.5px",
                      fontVariantNumeric: "tabular-nums",
                      // Zero is grey. A row of bold zeros reads as five
                      // problems; a quiet zero reads as "nothing happened".
                      color: n === 0 ? T.faint : key === "REJECTED" ? T.danger : T.ink,
                    }}
                  >
                    {n}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.15 }}>
                    <Box className="al-dot" sx={{ bgcolor: colorOf(key), opacity: n === 0 ? 0.35 : 1 }} />
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: T.muted, fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            <Box sx={{ flex: 1 }} />
            <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: T.body, fontWeight: 700 }}>
                {totalActions} action{totalActions === 1 ? "" : "s"}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: T.faint }}>
                {data.range.from} → {data.range.to} · {data.range.days} day
                {data.range.days === 1 ? "" : "s"}
              </Typography>
            </Box>
          </Box>

          {/* Said out loud. A log that quietly stops at its cap is a log you
              cannot rely on to answer "did anything else happen". */}
          {data.truncated && (
            <Alert
              severity="warning"
              icon={<WarningAmberRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: "10px", fontFamily: FONT, fontSize: 12.5, mb: 2 }}
            >
              This window holds more than {data.cap} actions; the most recent{" "}
              {data.cap} are shown. Narrow the dates or filter by job to see the rest.
            </Alert>
          )}

          {data.days.length === 0 && !state.loading && (
            <Box className="al-card al-in" sx={{ p: 5, textAlign: "center" }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 14.5, fontWeight: 700, color: T.strong }}>
                Nothing happened in this window
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: T.muted, mt: 0.5 }}>
                {jobId || type
                  ? "Try clearing the job or action filter, or widen the dates."
                  : "Widen the dates to look further back."}
              </Typography>
            </Box>
          )}

          {/* ── Day by day ─────────────────────────────────────────────── */}
          {data.days.map((day) => (
            <Box key={day.date} className="al-in" sx={{ mb: 2.5 }}>
              <Box className="al-dayhead">
                <Typography sx={{ fontFamily: FONT, fontSize: 14.5, fontWeight: 800, color: T.ink }}>
                  {dayHeading(day.date)}
                </Typography>
                {subHeading(day.date) && (
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: T.faint }}>
                    {subHeading(day.date)}
                  </Typography>
                )}
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: T.muted }}>
                  {summaryLine(day.totals)}
                </Typography>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Box className="al-card" sx={{ minWidth: TABLE_MIN, overflow: "hidden" }}>
                  <Box className="al-grid al-head">
                    <Box>Time</Box>
                    <Box>Action</Box>
                    <Box>Candidate</Box>
                    <Box>Job</Box>
                    <Box>Round</Box>
                    <Box sx={{ textAlign: "right" }}>By</Box>
                  </Box>

                  {day.entries.map((e) => {
                    const negative = familyOf(e.type) === "setback";
                    const warn = e.unexplained || e.undelivered;
                    return (
                      <Box key={e.id} className="al-row">
                        <Box className="al-grid">
                          <Box className="al-time">{clock(e.at)}</Box>

                          <Box className="al-action" sx={{ color: negative ? T.danger : T.ink }}>
                            <Box className="al-dot" sx={{ bgcolor: colorOf(e.type) }} />
                            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {e.label}
                            </Box>
                          </Box>

                          {/* The log is a way INTO the pipeline, not a dead
                              end: both the name and the job open the job. */}
                          <Box
                            className="al-name"
                            onClick={() => e.job_id && navigate(`/ats/job/${e.job_id}`)}
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={e.candidate_email || e.candidate_name}
                          >
                            {e.candidate_name}
                          </Box>

                          <Box
                            className="al-job"
                            onClick={() => e.job_id && navigate(`/ats/job/${e.job_id}`)}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: e.job_id ? "pointer" : "default",
                            }}
                            title={e.job_title}
                          >
                            {e.job_title}
                          </Box>

                          <Box className="al-move">
                            {e.to_stage ? (
                              <>
                                {e.from_stage && (
                                  <>
                                    <Box
                                      component="span"
                                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    >
                                      {e.from_stage}
                                    </Box>
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 13, color: T.faint, flexShrink: 0 }} />
                                  </>
                                )}
                                <Box
                                  component="span"
                                  sx={{
                                    color: T.strong,
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {e.to_stage}
                                </Box>
                              </>
                            ) : e.verdict ? (
                              <Chip tone="info">{e.verdict.replace(/_/g, " ")}</Chip>
                            ) : (
                              <Box component="span" sx={{ color: T.faint }}>
                                —
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                            {e.operator ? (
                              <Chip
                                tone="neutral"
                                title={e.operator.role || "operator"}
                                icon={<PersonOutlineRoundedIcon sx={{ fontSize: 12 }} />}
                              >
                                {e.operator.name}
                              </Chip>
                            ) : e.by_operator_expected ? (
                              <Chip
                                tone="warn"
                                title="This happened before the system recorded who performed each action"
                              >
                                not recorded
                              </Chip>
                            ) : (
                              <Chip tone="neutral" icon={<SmartToyOutlinedIcon sx={{ fontSize: 12 }} />}>
                                system
                              </Chip>
                            )}
                          </Box>

                          {/* A person's own words, quoted. The system's
                              description, grey. Same split as the candidate
                              trail — a rejection's reason is the thing being
                              looked for, and it should not read like an SMTP
                              log line. */}
                          {e.note ? (
                            <Box className="al-quote" data-neg={negative}>
                              {e.note}
                            </Box>
                          ) : (
                            e.detail && (
                              <Box className="al-detail" sx={{ color: warn ? T.warnInk : T.muted }}>
                                {warn && (
                                  <WarningAmberRoundedIcon
                                    sx={{ fontSize: 13, mr: 0.5, mb: "-2px", color: T.warnInk }}
                                  />
                                )}
                                {e.detail}
                              </Box>
                            )
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
